import { Navigate } from "react-router-dom";
import { authEnabled } from "../auth/config";
import { useAuth } from "../auth/useAuth";
import { NotFound } from "../components/DetailBits";
import { ArchitectureDiagram, DataPipelineDiagram } from "../components/ArchitectureDiagram";

// Admin-only architecture view. Public wrapper guards the useAuth() call (which needs MsalProvider,
// only mounted when authEnabled) — same pattern as the inline admin editors.
export function Architecture() {
  if (!authEnabled) return <NotFound />;
  return <ArchitectureGated />;
}

function ArchitectureGated() {
  const { isAdmin, ready } = useAuth();
  if (!ready) return null; // wait out the MSAL redirect before deciding
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="wrap section">
      <p className="eyebrow">Admin · system architecture</p>
      <h1 className="display" style={{ fontSize: 40, marginTop: 10 }}>
        How this is built
      </h1>
      <p className="section-sub" style={{ maxWidth: "64ch" }}>
        The deployed architecture end to end: a static React SPA at the edge, a thin serverless API for
        AI and content, and a build-time content pipeline. Visitor data (the journey) never leaves the
        browser. This view is admin-only.
      </p>

      <div className="arch-wrap card card-pad">
        <ArchitectureDiagram />
      </div>

      <div className="arch-legend">
        <span className="mono"><span className="arch-key arch-key-solid" /> request path</span>
        <span className="mono"><span className="arch-key arch-key-dash" /> identity · build · reconcile</span>
        <span className="mono muted">Deployed via AWS CDK (PDK monorepo) · region us-east-1</span>
      </div>

      <h2 className="display" style={{ fontSize: 28, marginTop: 44 }}>
        Content data pipeline
      </h2>
      <p className="section-sub" style={{ maxWidth: "64ch" }}>
        How content flows from raw sources to the public canon: the comprehensive résumé, accelerator
        and blog links, and local engagement folders are curated through a two-gate human review
        (REVIEW workbook + approved corpus) that originally seeded <code>resume.yaml</code> — now
        maintained directly in-app via Admin Edit (the content overlay). The build then derives the
        public corpus that grounds Ask AI and bring-your-own-AI.
      </p>

      <div className="arch-wrap card card-pad">
        <DataPipelineDiagram />
      </div>

      <div className="arch-legend">
        <span className="mono"><span className="arch-key arch-key-solid" /> current flow</span>
        <span className="mono"><span className="arch-key arch-key-dash" /> original seed · reconcile</span>
        <span className="mono muted">Curation gate: see the refresh-corpus skill (two-gate HITL approval)</span>
      </div>
    </div>
  );
}
